import React, { useState, useRef } from 'react';
import { Plus, Upload, Package, X, Loader2, CheckCircle, AlertCircle, FileSpreadsheet, Trash2, ChevronDown, ChevronUp, Image as ImageIcon, Video, Film } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categories = [
  'Electronics', 'Fashion', 'Furniture', 'Home', 'Vehicles', 'Books',
  'Sports', 'Food', 'Pets & Plants', 'Coupons', 'Services', 'Other'
];

const conditions = [
  { value: 'new', label: 'New' },
  { value: 'like_new', label: 'Like New' },
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair' },
  { value: 'poor', label: 'Poor' },
];

const emptyListing = () => ({
  title: '',
  description: '',
  category: 'Electronics',
  condition: 'new',
  images: [],
  city: '',
  state: '',
  pin_code: '',
  country: 'India',
  bidding_end_time: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
});

function MediaUploader({ images, onChange, prefix }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleFiles = async (files) => {
    const token = localStorage.getItem('admin_token');
    const fileList = Array.from(files);
    const allowedImage = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const allowedVideo = ['video/mp4', 'video/webm', 'video/ogg'];
    const maxImageSize = 10 * 1024 * 1024;
    const maxVideoSize = 100 * 1024 * 1024;

    setUploading(true);
    const newUrls = [...images];

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      const isVideo = allowedVideo.includes(file.type);
      const isImage = allowedImage.includes(file.type);

      if (!isImage && !isVideo) {
        toast.error(`${file.name}: Unsupported format`);
        continue;
      }
      if (isImage && file.size > maxImageSize) {
        toast.error(`${file.name}: Image exceeds 10MB`);
        continue;
      }
      if (isVideo && file.size > maxVideoSize) {
        toast.error(`${file.name}: Video exceeds 100MB`);
        continue;
      }

      const formData = new FormData();
      formData.append('file', file);
      const endpoint = isVideo ? '/api/upload/video' : '/api/upload/image';

      try {
        setProgress(Math.round(((i) / fileList.length) * 100));
        const res = await axios.post(`${API_URL}${endpoint}`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            const filePct = Math.round((e.loaded * 100) / e.total);
            setProgress(Math.round(((i + filePct / 100) / fileList.length) * 100));
          },
        });
        newUrls.push(`${API_URL}${res.data.url}`);
      } catch (err) {
        toast.error(`${file.name}: Upload failed`);
      }
    }

    onChange(newUrls);
    setUploading(false);
    setProgress(0);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files.length) handleFiles(e.dataTransfer.files);
  };

  const removeMedia = (index) => {
    onChange(images.filter((_, i) => i !== index));
  };

  const isVideoUrl = (url) => /\.(mp4|webm|ogg|mov)/.test(url) || url.includes('/videos/');

  return (
    <div>
      <label className="block text-xs font-medium text-slate-400 mb-1">Images / Videos</label>

      {/* Upload Zone */}
      <div
        data-testid={`${prefix}upload-zone`}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => !uploading && fileRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-all ${
          uploading ? 'border-indigo-500 bg-indigo-900/20' : 'border-slate-600 hover:border-indigo-500 hover:bg-slate-700/50'
        }`}
      >
        <input
          ref={fileRef}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm,video/ogg"
          onChange={(e) => handleFiles(e.target.files)}
          className="hidden"
          data-testid={`${prefix}file-input`}
        />
        {uploading ? (
          <div className="space-y-2">
            <Loader2 className="w-6 h-6 text-indigo-400 animate-spin mx-auto" />
            <p className="text-xs font-medium text-indigo-300">Uploading... {progress}%</p>
            <div className="w-full bg-slate-700 rounded-full h-1.5 max-w-[200px] mx-auto">
              <div className="bg-indigo-500 h-1.5 rounded-full transition-all" style={{ width: `${progress}%` }}></div>
            </div>
          </div>
        ) : (
          <div className="space-y-1">
            <div className="flex items-center justify-center gap-2">
              <Upload className="w-5 h-5 text-slate-400" />
              <ImageIcon className="w-5 h-5 text-slate-400" />
              <Film className="w-5 h-5 text-slate-400" />
            </div>
            <p className="text-xs font-medium text-slate-300">Drop images or videos here, or click to browse</p>
            <p className="text-[10px] text-slate-500">JPG, PNG, GIF, WebP (10MB) &middot; MP4, WebM, OGG (100MB)</p>
          </div>
        )}
      </div>

      {/* Uploaded Media Grid */}
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {images.map((url, i) => (
            <div key={i} className="relative group w-20 h-20 rounded-lg overflow-hidden border border-slate-600 bg-slate-700">
              {isVideoUrl(url) ? (
                <div className="w-full h-full flex items-center justify-center bg-slate-800">
                  <Video className="w-6 h-6 text-indigo-400" />
                </div>
              ) : (
                <img src={url} alt={`Media ${i + 1}`} className="w-full h-full object-cover" />
              )}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeMedia(i); }}
                className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`${prefix}remove-media-${i}`}
              >
                <X className="w-3 h-3 text-white" />
              </button>
              <span className="absolute bottom-0.5 left-0.5 text-[8px] bg-black/60 text-white px-1 rounded">
                {i + 1}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminCreateListings() {
  const [mode, setMode] = useState('single'); // 'single' or 'bulk'
  const [singleForm, setSingleForm] = useState(emptyListing());
  const [bulkListings, setBulkListings] = useState([emptyListing(), emptyListing()]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [expandedBulk, setExpandedBulk] = useState(0);
  const csvInputRef = useRef(null);

  const handleSingleSubmit = async (e) => {
    e.preventDefault();
    if (!singleForm.title || !singleForm.description) {
      toast.error('Title and description are required');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const payload = {
        ...singleForm,
        bidding_end_time: new Date(singleForm.bidding_end_time).toISOString(),
      };
      const res = await axios.post(`${API_URL}/api/admin/listings/create`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Listing created successfully!');
      setResults({ type: 'single', data: res.data });
      setSingleForm(emptyListing());
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create listing');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkSubmit = async () => {
    const valid = bulkListings.filter(l => l.title && l.description);
    if (valid.length === 0) {
      toast.error('At least one listing with title and description is required');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const payload = {
        listings: valid.map(l => ({
          ...l,
          bidding_end_time: new Date(l.bidding_end_time).toISOString(),
        })),
      };
      const res = await axios.post(`${API_URL}/api/admin/listings/bulk`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(res.data.message);
      setResults({ type: 'bulk', data: res.data });
      setBulkListings([emptyListing(), emptyListing()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk creation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCSVUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target.result;
        const lines = text.split('\n').filter(l => l.trim());
        if (lines.length < 2) {
          toast.error('CSV must have a header row and at least one data row');
          return;
        }
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        const titleIdx = headers.indexOf('title');
        const descIdx = headers.indexOf('description');
        const catIdx = headers.indexOf('category');
        const condIdx = headers.indexOf('condition');
        const cityIdx = headers.indexOf('city');
        const stateIdx = headers.indexOf('state');
        const countryIdx = headers.indexOf('country');
        const pinIdx = headers.indexOf('pin_code');
        const endIdx = headers.indexOf('bidding_end_time');

        if (titleIdx === -1 || descIdx === -1) {
          toast.error('CSV must have "title" and "description" columns');
          return;
        }

        const parsed = [];
        for (let i = 1; i < lines.length; i++) {
          const cols = lines[i].split(',').map(c => c.trim());
          if (!cols[titleIdx]) continue;
          parsed.push({
            title: cols[titleIdx] || '',
            description: cols[descIdx] || '',
            category: (catIdx >= 0 && cols[catIdx]) || 'Other',
            condition: (condIdx >= 0 && cols[condIdx]) || 'new',
            images: [],
            city: (cityIdx >= 0 && cols[cityIdx]) || '',
            state: (stateIdx >= 0 && cols[stateIdx]) || '',
            pin_code: (pinIdx >= 0 && cols[pinIdx]) || '',
            country: (countryIdx >= 0 && cols[countryIdx]) || 'India',
            bidding_end_time: (endIdx >= 0 && cols[endIdx]) || new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0],
          });
        }
        if (parsed.length === 0) {
          toast.error('No valid rows found in CSV');
          return;
        }
        setBulkListings(parsed);
        setExpandedBulk(0);
        toast.success(`Imported ${parsed.length} listings from CSV`);
      } catch {
        toast.error('Failed to parse CSV file');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const updateBulkItem = (index, field, value) => {
    setBulkListings(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };

  const removeBulkItem = (index) => {
    if (bulkListings.length <= 1) return;
    setBulkListings(prev => prev.filter((_, i) => i !== index));
    if (expandedBulk >= bulkListings.length - 1) setExpandedBulk(Math.max(0, bulkListings.length - 2));
  };

  const ListingFormFields = ({ data, onChange, prefix = '' }) => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="md:col-span-2">
        <label className="block text-xs font-medium text-slate-400 mb-1">Title *</label>
        <input
          type="text"
          data-testid={`${prefix}listing-title`}
          value={data.title}
          onChange={(e) => onChange('title', e.target.value)}
          className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
          placeholder="Product title"
        />
      </div>
      <div className="md:col-span-2">
        <label className="block text-xs font-medium text-slate-400 mb-1">Description *</label>
        <textarea
          data-testid={`${prefix}listing-description`}
          value={data.description}
          onChange={(e) => onChange('description', e.target.value)}
          rows={3}
          className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm resize-none"
          placeholder="Product description"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-400 mb-1">Category</label>
        <select
          data-testid={`${prefix}listing-category`}
          value={data.category}
          onChange={(e) => onChange('category', e.target.value)}
          className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-indigo-500 text-sm"
        >
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-400 mb-1">Condition</label>
        <select
          data-testid={`${prefix}listing-condition`}
          value={data.condition}
          onChange={(e) => onChange('condition', e.target.value)}
          className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-indigo-500 text-sm"
        >
          {conditions.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-400 mb-1">City</label>
        <input
          type="text"
          value={data.city}
          onChange={(e) => onChange('city', e.target.value)}
          className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-indigo-500 text-sm"
          placeholder="City"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-400 mb-1">State</label>
        <input
          type="text"
          value={data.state}
          onChange={(e) => onChange('state', e.target.value)}
          className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-indigo-500 text-sm"
          placeholder="State"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-400 mb-1">Country</label>
        <input
          type="text"
          value={data.country}
          onChange={(e) => onChange('country', e.target.value)}
          className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-indigo-500 text-sm"
          placeholder="Country"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-400 mb-1">Pin Code</label>
        <input
          type="text"
          value={data.pin_code}
          onChange={(e) => onChange('pin_code', e.target.value)}
          className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-indigo-500 text-sm"
          placeholder="Pin Code"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-400 mb-1">Bidding End Date</label>
        <input
          type="date"
          data-testid={`${prefix}listing-end-date`}
          value={data.bidding_end_time}
          onChange={(e) => onChange('bidding_end_time', e.target.value)}
          className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-indigo-500 text-sm"
        />
      </div>
      <div className="md:col-span-2">
        <MediaUploader
          images={data.images || []}
          onChange={(imgs) => onChange('images', imgs)}
          prefix={prefix}
        />
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-create-listings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Create Listings</h1>
          <p className="text-slate-400 text-sm mt-1">Create product listings under your admin account</p>
        </div>
      </div>

      {/* Mode Toggle */}
      <div className="flex items-center gap-2 bg-slate-800 p-1 rounded-xl w-fit">
        <button
          data-testid="mode-single"
          onClick={() => { setMode('single'); setResults(null); }}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            mode === 'single' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'
          }`}
        >
          <Package className="w-4 h-4" /> Single Listing
        </button>
        <button
          data-testid="mode-bulk"
          onClick={() => { setMode('bulk'); setResults(null); }}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            mode === 'bulk' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'
          }`}
        >
          <FileSpreadsheet className="w-4 h-4" /> Bulk Create
        </button>
      </div>

      {/* Results Banner */}
      {results && (
        <div className={`p-4 rounded-xl border ${results.data.errors?.length > 0 ? 'bg-amber-900/20 border-amber-700' : 'bg-green-900/20 border-green-700'}`}>
          <div className="flex items-start gap-3">
            {results.data.errors?.length > 0 ? (
              <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5" />
            ) : (
              <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
            )}
            <div>
              <p className="font-medium text-white">{results.data.message || 'Listing created successfully!'}</p>
              {results.type === 'bulk' && results.data.created && (
                <div className="mt-2 space-y-1">
                  {results.data.created.map((c, i) => (
                    <p key={i} className="text-xs text-green-400">Created: {c.title}</p>
                  ))}
                  {results.data.errors?.map((e, i) => (
                    <p key={i} className="text-xs text-red-400">Failed: {e.title} — {e.error}</p>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => setResults(null)} className="ml-auto text-slate-400 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Single Listing Form */}
      {mode === 'single' && (
        <form onSubmit={handleSingleSubmit} className="bg-slate-800 rounded-xl border border-slate-700 p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Package className="w-5 h-5 text-indigo-400" /> New Listing
          </h2>
          <ListingFormFields
            data={singleForm}
            onChange={(field, value) => setSingleForm(prev => ({ ...prev, [field]: value }))}
            prefix="single-"
          />
          <div className="mt-6 flex justify-end">
            <button
              type="submit"
              disabled={loading}
              data-testid="single-submit"
              className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Create Listing
            </button>
          </div>
        </form>
      )}

      {/* Bulk Creation */}
      {mode === 'bulk' && (
        <div className="space-y-4">
          {/* Toolbar */}
          <div className="flex items-center gap-3 flex-wrap">
            <button
              data-testid="add-bulk-item"
              onClick={() => { setBulkListings(prev => [...prev, emptyListing()]); setExpandedBulk(bulkListings.length); }}
              className="flex items-center gap-1.5 px-3 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 text-sm font-medium transition-colors"
            >
              <Plus className="w-4 h-4" /> Add Listing
            </button>
            <button
              data-testid="csv-import-btn"
              onClick={() => csvInputRef.current?.click()}
              className="flex items-center gap-1.5 px-3 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 text-sm font-medium transition-colors"
            >
              <Upload className="w-4 h-4" /> Import CSV
            </button>
            <input
              ref={csvInputRef}
              type="file"
              accept=".csv"
              onChange={handleCSVUpload}
              className="hidden"
              data-testid="csv-file-input"
            />
            <span className="text-xs text-slate-500">
              CSV format: title, description, category, condition, city, state, country, pin_code, bidding_end_time
            </span>
            <div className="ml-auto text-sm text-slate-400">
              {bulkListings.length} listing{bulkListings.length !== 1 ? 's' : ''}
            </div>
          </div>

          {/* Bulk Items Accordion */}
          <div className="space-y-2">
            {bulkListings.map((item, index) => (
              <div key={index} className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                {/* Accordion Header */}
                <button
                  type="button"
                  onClick={() => setExpandedBulk(expandedBulk === index ? -1 : index)}
                  className="w-full flex items-center justify-between p-4 hover:bg-slate-750 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="w-7 h-7 bg-indigo-600/30 text-indigo-400 rounded-lg flex items-center justify-center text-xs font-bold">
                      {index + 1}
                    </span>
                    <div className="text-left">
                      <p className="text-sm font-medium text-white">
                        {item.title || `Listing ${index + 1}`}
                      </p>
                      <p className="text-xs text-slate-500">
                        {item.category} &middot; {conditions.find(c => c.value === item.condition)?.label || item.condition}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {bulkListings.length > 1 && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); removeBulkItem(index); }}
                        className="p-1.5 text-slate-500 hover:text-red-400 transition-colors"
                        data-testid={`remove-bulk-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                    {expandedBulk === index ? (
                      <ChevronUp className="w-4 h-4 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    )}
                  </div>
                </button>

                {/* Accordion Body */}
                {expandedBulk === index && (
                  <div className="px-4 pb-4 border-t border-slate-700 pt-4">
                    <ListingFormFields
                      data={item}
                      onChange={(field, value) => updateBulkItem(index, field, value)}
                      prefix={`bulk-${index}-`}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Bulk Submit */}
          <div className="flex justify-end">
            <button
              onClick={handleBulkSubmit}
              disabled={loading}
              data-testid="bulk-submit"
              className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Create {bulkListings.filter(l => l.title && l.description).length} Listing{bulkListings.filter(l => l.title && l.description).length !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
